import React, { useState, useEffect } from 'react';
import { Settings, Loader2, Save, CheckCircle, XCircle, Target, Activity, Download, BookOpen, Workflow, Sparkles } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SmartToolSettings = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [settings, setSettings] = useState({
    lead_scoring_enabled: true,
    lead_assignment_enabled: true,
    lead_pull_enabled: true,
    knowledge_center_enabled: true,
    workflows_enabled: true,
    ai_followup_enabled: true,
  });

  const tools = [
    {
      key: 'lead_scoring_enabled',
      icon: Target,
      label: 'Lead Scoring',
      description: 'Automatically score leads based on multiple factors like source, response time, and engagement'
    },
    {
      key: 'lead_assignment_enabled',
      icon: Activity,
      label: 'Lead Assignment',
      description: 'Auto-assign leads to visa consultants using round-robin or rule-based distribution'
    },
    {
      key: 'lead_pull_enabled',
      icon: Download,
      label: 'Lead Pull',
      description: 'Allow sales users to pull unattended leads from the pool based on rules'
    },
    {
      key: 'knowledge_center_enabled',
      icon: BookOpen,
      label: 'Knowledge Center',
      description: 'AI-powered knowledge base for storing and retrieving answers to customer questions'
    },
    {
      key: 'workflows_enabled',
      icon: Workflow,
      label: 'Workflows',
      description: 'Automated workflow triggers for tasks, notifications, and follow-ups'
    },
    {
      key: 'ai_followup_enabled',
      icon: Sparkles,
      label: 'AI Follow-Up',
      description: 'AI-generated follow-up suggestions and email/WhatsApp templates'
    }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/smart-tools/settings`, {
        headers: getAuthHeader()
      });
      setSettings(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/smart-tools/settings`,
        settings,
        { headers: getAuthHeader() }
      );
      
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to save settings' 
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-loading">
        <Loader2 className="spinner" size={32} />
        <p>Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="smart-tool-settings-page">
      <div className="settings-header">
        <div className="header-content">
          <Settings className="header-icon" size={28} />
          <div>
            <h1>Smart Lead Tools Settings</h1>
            <p>Enable or disable smart lead tools for your CRM</p>
          </div>
        </div>
        <button 
          className="save-button" 
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <Loader2 className="spinner" size={18} />
              Saving...
            </>
          ) : (
            <>
              <Save size={18} />
              Save Changes
            </>
          )}
        </button>
      </div>

      {message.text && (
        <div className={`message-banner ${message.type}`}>
          {message.type === 'success' ? (
            <CheckCircle size={20} />
          ) : (
            <XCircle size={20} />
          )}
          <span>{message.text}</span>
        </div>
      )}

      <div className="tools-grid">
        {tools.map(tool => {
          const Icon = tool.icon;
          const isEnabled = settings[tool.key];
          
          return (
            <div key={tool.key} className="tool-card">
              <div className="tool-header">
                <div className="tool-icon-wrapper" style={{ backgroundColor: isEnabled ? '#A08EA8' : '#ccc' }}>
                  <Icon size={24} color="white" />
                </div>
                <div className="tool-info">
                  <h3>{tool.label}</h3>
                  <p>{tool.description}</p>
                </div>
              </div>
              <div className="tool-toggle">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={() => handleToggle(tool.key)}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span className={`status-label ${isEnabled ? 'enabled' : 'disabled'}`}>
                  {isEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="settings-info">
        <div className="info-card">
          <h4>ℹ️ Important Notes</h4>
          <ul>
            <li>Disabled tools will be hidden from the sidebar menu for all users</li>
            <li>Existing data will not be affected when tools are disabled</li>
            <li>Re-enabling a tool will restore its functionality immediately</li>
            <li>Only Super Admin can modify these settings</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SmartToolSettings;
